import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import {
  changeManagementService,
  ChangeOrderFull,
} from '../../services/change-management.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  pending: 'Pendiente',
  client_approved: 'Aprobado cliente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  executed: 'Ejecutado',
};

export function ChangeOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [order, setOrder] = useState<ChangeOrderFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  const canApprove = ['sys_admin', 'general_manager', 'project_lead'].includes(
    user?.role ?? ''
  );
  const canExecute = ['sys_admin', 'general_manager', 'project_lead'].includes(
    user?.role ?? ''
  );

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await changeManagementService.getChangeOrder(id);
      setOrder(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleApprove = async () => {
    if (!id) return;
    if (!(await confirm({ message: '¿Aprobar esta orden de cambio?', danger: false }))) return;
    setIsActing(true);
    try {
      await changeManagementService.approveChangeOrder(id);
      showSuccess('Orden aprobada');
      await load();
    } catch (e: any) {
      showError(e.message || 'Error al aprobar');
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    if (!reason.trim()) {
      showError('Escribe un motivo');
      return;
    }
    setIsActing(true);
    try {
      await changeManagementService.rejectChangeOrder(id, reason.trim());
      showSuccess('Orden rechazada');
      setShowReject(false);
      setReason('');
      await load();
    } catch (e: any) {
      showError(e.message || 'Error al rechazar');
    } finally {
      setIsActing(false);
    }
  };

  const handleExecute = async () => {
    if (!id) return;
    if (
      !(await confirm({
        message: '¿Ejecutar esta orden? El presupuesto del proyecto se actualizará.',
        danger: true,
        confirmLabel: 'Ejecutar',
      }))
    )
      return;
    setIsActing(true);
    try {
      await changeManagementService.executeChangeOrder(id);
      showSuccess('Orden ejecutada');
      await load();
    } catch (e: any) {
      showError(e.message || 'Error al ejecutar');
    } finally {
      setIsActing(false);
    }
  };

  if (loading) return <LoadingState message="Cargando orden…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!order) return <ErrorState message="Orden no encontrada" onRetry={load} />;

  const fc = (n: number) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(n || 0);

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 flex items-center gap-1"
      >
        ← Volver
      </button>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-[11px] font-mono text-slate-500">{order.number}</p>
          <StatusBadge status={order.status} label={STATUS_LABELS[order.status]} />
        </div>
        <h1 className="text-lg font-bold text-slate-900 mb-1">{order.title}</h1>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          {order.description}
        </p>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-slate-500">Solicitante</p>
            <p className="font-medium text-slate-900">{order.requestedByName ?? '—'}</p>
          </div>
          <div>
            <p className="text-slate-500">Prioridad</p>
            <p className="font-medium text-slate-900 capitalize">{order.priority}</p>
          </div>
          <div>
            <p className="text-slate-500">Categoría</p>
            <p className="font-medium text-slate-900 capitalize">{order.category}</p>
          </div>
          <div>
            <p className="text-slate-500">Fecha</p>
            <p className="font-medium text-slate-900">
              {new Date(order.createdAt).toLocaleDateString('es-MX')}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-bold text-slate-900 mb-3">Impacto económico</h2>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Presupuesto original</span>
            <span className="font-medium">{fc(order.impact?.originalBudget ?? 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Cambio neto</span>
            <span
              className={`font-semibold ${
                (order.impact?.impact ?? 0) >= 0 ? 'text-red-600' : 'text-emerald-600'
              }`}
            >
              {fc(order.impact?.impact ?? 0)}
            </span>
          </div>
          <div className="flex justify-between pt-1 border-t border-slate-100">
            <span className="font-semibold">Nuevo presupuesto</span>
            <span className="font-bold text-primary">
              {fc(order.impact?.newBudget ?? 0)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-bold text-slate-900 mb-3">
          Partidas ({order.items?.length ?? 0})
        </h2>
        <ul className="space-y-2">
          {(order.items ?? []).map((item, i) => (
            <li key={i} className="bg-slate-50 rounded-lg p-2.5 text-xs">
              <div className="flex justify-between gap-2 mb-0.5">
                <p className="font-medium text-slate-900 truncate">{item.description}</p>
                <span
                  className={`font-semibold ${
                    item.type === 'deduction' ? 'text-emerald-600' : 'text-red-600'
                  }`}
                >
                  {item.type === 'deduction' ? '-' : '+'}
                  {fc((item.quantity || 0) * (item.unitPrice || 0))}
                </span>
              </div>
              <p className="text-slate-500">
                {item.quantity} {item.unit} × {fc(item.unitPrice)}
              </p>
            </li>
          ))}
        </ul>
      </div>

      {order.clientSignatureName && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-emerald-800 mb-0.5">
            Aprobado por el cliente
          </p>
          <p className="text-sm text-emerald-700">
            {order.clientSignatureName}
            {order.clientApprovedAt && (
              <span className="text-xs text-emerald-600 ml-1">
                · {new Date(order.clientApprovedAt).toLocaleDateString('es-MX')}
              </span>
            )}
          </p>
        </div>
      )}

      {order.status === 'pending' && canApprove && (
        <div className="flex gap-2">
          <button
            onClick={() => setShowReject(true)}
            disabled={isActing}
            className="flex-1 py-3 rounded-lg bg-red-50 text-red-600 font-semibold text-sm disabled:opacity-50"
          >
            Rechazar
          </button>
          <button
            onClick={handleApprove}
            disabled={isActing}
            className="flex-1 py-3 rounded-lg bg-emerald-600 text-white font-semibold text-sm disabled:opacity-50"
          >
            {isActing ? 'Procesando…' : 'Aprobar'}
          </button>
        </div>
      )}

      {(order.status === 'approved' || order.status === 'client_approved') && canExecute && (
        <button
          onClick={handleExecute}
          disabled={isActing}
          className="w-full py-3 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
        >
          {isActing ? 'Ejecutando…' : 'Ejecutar orden'}
        </button>
      )}

      {showReject && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <h2 className="text-lg font-bold mb-3">Motivo de rechazo</h2>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowReject(false)}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={isActing || !reason.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-semibold text-sm disabled:opacity-50"
              >
                {isActing ? 'Procesando…' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}